import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, FormControl, InputLabel, Select,
    MenuItem, TextField, Stack, Button, useMediaQuery
} from '@mui/material';
import {
    LocalizationProvider,
    DatePicker,
} from '@mui/x-date-pickers';
import { koKR } from '@mui/x-date-pickers/locales';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import api from '../../../../api/axios';
import MessageLogTable from './MessageLogTable';

dayjs.locale('ko');

export default function ChatLogPage() {
    const [categories, setCategories] = useState([]);
    const [selected, setSelected] = useState('');
    const [keyword, setKeyword] = useState('');
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs());
    const isMobile = useMediaQuery('(max-width:600px)');

    useEffect(() => {
        api.get('/api/chat/categories/')
            .then(res => setCategories(res.data.categories || []))
            .catch(() => alert('카테고리 목록을 불러오지 못했습니다.'));
    }, []);

    const selectedName =
        selected !== 'all'
            ? categories.find(c => c.id === selected)?.name || ''
            : '';

    const triggerExcelDownload = () =>
        window.dispatchEvent(new Event('exportToExcel'));

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="ko"
            localeText={koKR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            <Box sx={{ p: 3 }}>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    {isMobile ? (
                        // 모바일 레이아웃
                        <Stack spacing={2}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>채팅방</InputLabel>
                                <Select
                                    value={selected}
                                    label="채팅방"
                                    onChange={e => setSelected(e.target.value)}
                                >
                                    <MenuItem value="all">모든카테고리</MenuItem>
                                    {categories.map(cat => (
                                        <MenuItem key={cat.id} value={cat.id}>
                                            {cat.emoji} {cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <DatePicker
                                label="시작일"
                                value={startDate}
                                onChange={v => setStartDate(v)}
                                format="YYYY-MM-DD"
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    },
                                }}
                            />

                            <DatePicker
                                label="종료일"
                                value={endDate}
                                onChange={v => setEndDate(v)}
                                format="YYYY-MM-DD"
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    },
                                }}
                            />

                            <TextField
                                label="닉네임/메시지"
                                size="small"
                                value={keyword}
                                onChange={e => setKeyword(e.target.value)}
                                fullWidth
                            />

                            <Button
                                variant="outlined"
                                size="medium"
                                disabled={!selected}
                                onClick={triggerExcelDownload}
                                fullWidth
                                sx={{ height: 40 }}
                            >
                                엑셀로 저장
                            </Button>
                        </Stack>
                    ) : (
                        // PC 레이아웃
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            flexWrap="nowrap"
                        >
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>채팅방</InputLabel>
                                <Select
                                    value={selected}
                                    label="채팅방"
                                    onChange={e => setSelected(e.target.value)}
                                >
                                    <MenuItem value="all">모든카테고리</MenuItem>
                                    {categories.map(cat => (
                                        <MenuItem key={cat.id} value={cat.id}>
                                            {cat.emoji} {cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <DatePicker
                                label="시작일"
                                value={startDate}
                                onChange={v => setStartDate(v)}
                                format="YYYY-MM-DD"
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: { minWidth: 140 },
                                    },
                                }}
                            />

                            <DatePicker
                                label="종료일"
                                value={endDate}
                                onChange={v => setEndDate(v)}
                                format="YYYY-MM-DD"
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: { minWidth: 140 },
                                    },
                                }}
                            />

                            <TextField
                                label="닉네임/메시지"
                                size="small"
                                value={keyword}
                                onChange={e => setKeyword(e.target.value)}
                                sx={{ minWidth: 200, flexGrow: 1 }}
                            />

                            <Button
                                variant="outlined"
                                size="medium"
                                disabled={!selected}
                                onClick={triggerExcelDownload}
                                sx={{
                                    height: 40,
                                    whiteSpace: 'nowrap',
                                    width: 'auto',       // ✅ 너비 고정
                                    minWidth: 120,       // ✅ 최소 너비만 확보
                                    flex: 'none',        // ✅ flex 컨테이너 영향 제거
                                }}
                            >
                                엑셀로 저장
                            </Button>
                        </Stack>
                    )}
                </Paper>

                {selected ? (
                    <MessageLogTable
                        categoryId={selected}
                        categoryName={selectedName}
                        keyword={keyword}
                        startDate={startDate.format('YYYY-MM-DD')}
                        endDate={endDate.format('YYYY-MM-DD')}
                    />
                ) : (
                    <Typography color="text.secondary">
                        채팅방을 선택하면 메시지 로그가 표시됩니다.
                    </Typography>
                )}
            </Box>
        </LocalizationProvider>
    );
}
